/**
 * Attention / notification rules for active orders (web office).
 * Kept in sync with Orders tab badges and sidebar / list indicators.
 */

export function getOrderClientSignature(order) {
  if (!order || typeof order !== 'object') return null;
  const raw = order.client_signature_json ?? order.client_signature;
  return raw && typeof raw === 'object' ? raw : null;
}

export function getServicebonHistoryEntries(sig) {
  if (!sig || typeof sig !== 'object') return [];
  const h = sig.servicebon_history;
  return Array.isArray(h) ? h.filter((x) => x && typeof x === 'object') : [];
}

/** Signed servicebon rounds on a closed order (history + final signed current). */
export function countServicebonArchiveRounds(order) {
  const sig = getOrderClientSignature(order);
  if (!sig) return 0;
  let n = getServicebonHistoryEntries(sig).length;
  const sb = sig.servicebon;
  if (sb && typeof sb === 'object' && String(sb.data || '').trim()) n += 1;
  return n;
}

/**
 * Active orders that belong on the Service menu — open service rounds only (not mobile-completed).
 */
export function isOrderInActiveServiceQueue(order) {
  if (!isOrderInActiveWorkList(order)) return false;
  const mobile = String(order.mobile_work_status || '').trim().toLowerCase();
  if (mobile === 'completed') return false;

  const sig = getOrderClientSignature(order);
  if (!sig) return false;

  if (mobile === 'awaiting_servicebon') return true;

  const attention = getServicebonAttentionKind(order);
  if (attention === 'need_web_text' || attention === 'signed_mobile') return true;

  const sb = sig.servicebon && typeof sig.servicebon === 'object' ? sig.servicebon : null;

  // Opleverbon restpunten: servicebon still required.
  if (sig.data && String(sig.pending_work || '').trim()) {
    if (!sb || !String(sb.data || '').trim()) return true;
  }

  // Reopened / follow-up service: new unsigned servicebon round.
  if (getServicebonHistoryEntries(sig).length > 0) {
    if (!sb || !String(sb.data || '').trim()) return true;
  }

  return false;
}

/** Mobile-completed service orders waiting for office to mark complete (not in Closed yet). */
export function isOrderPendingOfficeCloseOnService(order) {
  if (!getPendingOfficeAttention(order)) return false;
  if (String(order.mobile_work_status || '').trim().toLowerCase() !== 'completed') return false;
  const sig = getOrderClientSignature(order);
  if (!sig || !String(sig.data || '').trim()) return false;
  const sb = sig.servicebon && typeof sig.servicebon === 'object' ? sig.servicebon : null;
  if (sb && String(sb.data || '').trim()) return true;
  if (getServicebonHistoryEntries(sig).length > 0) return true;
  if (String(sig.pending_work || '').trim()) return true;
  return false;
}

/** Service menu list: open rounds + awaiting office confirmation after mobile servicebon. */
export function isOrderOnServicePageActiveList(order) {
  return isOrderInActiveServiceQueue(order) || isOrderPendingOfficeCloseOnService(order);
}

/** Montage-phase orders only (excludes service-queue work and service awaiting office close). */
export function isOrderInMontageWorkList(order) {
  if (!isOrderInActiveWorkList(order)) return false;
  if (isOrderPendingOfficeCloseOnService(order)) return false;
  return !isOrderInActiveServiceQueue(order);
}

/** Assigned service orders that still need a slot on the planning calendar. */
export function isOrderAwaitingServicePlanning(order) {
  if (!isOrderInActiveServiceQueue(order)) return false;
  if (String(order?.status || '') !== 'assigned') return false;
  return !String(order.scheduled_date || order.montagedatum || '').trim();
}

/** Sidebar pool on Planning: new opleverbon orders + open service orders awaiting a schedule. */
export function isOrderOnPlanningSidebar(order) {
  if (order?.status === 'unassigned') return true;
  return isOrderAwaitingServicePlanning(order);
}

/** Default document tab when opening an order from the planning calendar. */
export function getDefaultOrderViewTab(order) {
  return isOrderInActiveServiceQueue(order) ? 'servicebon' : 'opleverbon';
}

/** Link from a planning calendar card to the correct order document. */
export function getPlanningOrderViewPath(order) {
  const id = order?.id;
  if (id == null) return '/orders';
  const params = new URLSearchParams({
    view: String(id),
    from: 'planning',
    tab: getDefaultOrderViewTab(order),
  });
  return `/orders?${params.toString()}`;
}

/** Closed orders with at least one signed servicebon (current or archived). */
export function isOrderWithServicebonArchive(order) {
  if (!isOrderClosedCompleted(order)) return false;
  return countServicebonArchiveRounds(order) > 0;
}

export function countActiveServiceQueueOrders(orders) {
  if (!Array.isArray(orders)) return 0;
  return orders.filter((o) => serviceOrderNeedsAttention(o)).length;
}

/** Same filter as OrdersPage `activeOrders` (excludes mobile-completed unless balance release pending). */
export function isOrderInActiveWorkList(order) {
  if (String(order.mobile_work_status || '') !== 'completed') return true;
  return Boolean(order.pending_closed_release);
}

/** Closed orders list: mobile done and office released — no tab/list attention markers. */
export function isOrderClosedCompleted(order) {
  return (
    String(order?.mobile_work_status || '').trim().toLowerCase() === 'completed' &&
    !order?.pending_closed_release
  );
}

export function getOpleverbonAttention(order) {
  if (!order || isOrderClosedCompleted(order)) return false;
  if (order.pending_closed_release) return false;
  const sig = getOrderClientSignature(order);
  if (!sig || !String(sig.data || '').trim()) return false;

  const sb = sig.servicebon && typeof sig.servicebon === 'object' ? sig.servicebon : null;
  if (sb && String(sb.data || '').trim()) {
    return false;
  }

  const m = String(order.mobile_work_status || '').trim().toLowerCase();
  if (m === 'awaiting_servicebon') {
    const pw = sb ? String(sb.pending_work || '').trim() : '';
    if (pw) {
      return false;
    }
  }

  return true;
}

/** @returns {'need_web_text' | 'signed_mobile' | null} */
export function getServicebonAttentionKind(order) {
  if (!order || isOrderClosedCompleted(order)) return null;
  if (order.pending_closed_release) return null;
  const sig = getOrderClientSignature(order);
  if (!sig || !String(sig.data || '').trim()) return null;
  const sb = sig.servicebon && typeof sig.servicebon === 'object' ? sig.servicebon : null;
  if (sb && String(sb.data || '').trim()) return 'signed_mobile';
  const m = String(order.mobile_work_status || '').trim().toLowerCase();
  if (m === 'awaiting_servicebon') {
    const pw = sb ? String(sb.pending_work || '').trim() : '';
    if (!pw) return 'need_web_text';
    return null;
  }
  return null;
}

export function getPendingOfficeAttention(order) {
  return Boolean(order?.pending_closed_release);
}

export function getWorkerNotesEntries(order) {
  const raw = order?.worker_notes_json;
  return Array.isArray(raw) ? raw.filter((n) => n && typeof n === 'object') : [];
}

function getLatestWorkerNoteTime(order) {
  const notes = getWorkerNotesEntries(order);
  let latest = 0;
  let hasTimestamp = false;
  for (const note of notes) {
    const raw = String(note.timestamp || '').trim();
    if (!raw) continue;
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) continue;
    hasTimestamp = true;
    if (dt.getTime() > latest) latest = dt.getTime();
  }
  return { latest, hasNotes: notes.length > 0, hasTimestamp };
}

/** Mobile worker note not yet opened on the web Notes tab. */
export function hasUnreadWorkerNotes(order) {
  const { latest, hasNotes, hasTimestamp } = getLatestWorkerNoteTime(order);
  if (!hasNotes) return false;
  if (!hasTimestamp) return true;

  const seenRaw = order?.worker_notes_office_seen_at;
  if (!seenRaw) return true;
  const seen = new Date(seenRaw);
  if (Number.isNaN(seen.getTime())) return true;
  return latest > seen.getTime();
}

export function getClosedOrderCompletedTime(order) {
  const raw = order?.client_signature_json?.completed_at || order?.created_at;
  if (!raw) return 0;
  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
}

export function countClosedOrdersWithUnreadWorkerNotes(orders) {
  if (!Array.isArray(orders)) return 0;
  return orders.filter((o) => isOrderClosedCompleted(o) && hasUnreadWorkerNotes(o)).length;
}

export function orderNeedsAttention(order) {
  if (hasUnreadWorkerNotes(order)) return true;
  if (!isOrderInActiveWorkList(order)) return false;
  if (isOrderInActiveServiceQueue(order)) {
    return getPendingOfficeAttention(order);
  }
  return (
    getOpleverbonAttention(order) ||
    Boolean(getServicebonAttentionKind(order)) ||
    getPendingOfficeAttention(order)
  );
}

/** Service menu sidebar dot — servicebon / post-opleverbon / awaiting office close. */
export function serviceOrderNeedsAttention(order) {
  if (isOrderPendingOfficeCloseOnService(order)) return true;
  if (!isOrderInActiveServiceQueue(order)) return false;
  return (
    Boolean(getServicebonAttentionKind(order)) ||
    getOpleverbonAttention(order) ||
    String(order.mobile_work_status || '').trim().toLowerCase() === 'awaiting_servicebon'
  );
}

export function countActiveOrdersNeedingAttention(orders) {
  if (!Array.isArray(orders)) return 0;
  return orders.filter((o) => isOrderInMontageWorkList(o) && orderNeedsAttention(o)).length;
}
